import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Coins, FileScan, HardDrive, Infinity as InfinityIcon, Download, Loader2,
  CheckCircle2, Sparkles, Receipt, Gauge, Users, CreditCard,
} from 'lucide-react';
import { brandColors } from '../components/Brand';

const fmtINR = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

// Parse a backend timestamp as UTC (append 'Z' when no timezone offset is present)
// and render it in IST (Asia/Kolkata) so users see their local time, not GMT.
const toUtcDate = (ts) => {
  if (!ts) return null;
  let s = String(ts);
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s.replace(' ', 'T') + 'Z';
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const fmtIST = (ts) => {
  const d = toUtcDate(ts);
  if (!d) return '—';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  }) + ' IST';
};
const fmtISTDate = (ts) => {
  const d = toUtcDate(ts);
  if (!d) return '—';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
  });
};

// Yearly savings vs. paying monthly for 12 months. Returns 0 when not applicable.
const yearlySavingsPct = (plan) => {
  const m = Number(plan?.monthly_price || 0);
  const y = Number(plan?.yearly_price || 0);
  if (m <= 0 || y <= 0) return 0;
  const annualMonthly = m * 12;
  if (y >= annualMonthly) return 0;
  return Math.round(((annualMonthly - y) / annualMonthly) * 100);
};

const TenantSubscription = ({ embedded = false }) => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [packages, setPackages] = useState(null);
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState('monthly');
  const [invoices, setInvoices] = useState([]);
  const [txns, setTxns] = useState([]);
  const [payments, setPayments] = useState([]);
  const [buying, setBuying] = useState('');
  const [polling, setPolling] = useState(false);

  const load = useCallback(async () => {
    const h = getAuthHeaders();

    // Use Promise.allSettled so one failed call cannot block the entire page.
    // Each call resolves independently — failures set a safe default instead of
    // leaving state as null and causing an infinite loading spinner.
    const [sub, pkg, inv, tx, pl, pay] = await Promise.allSettled([
      api.revMySubscription(h),
      api.revPackages(h),
      api.revListInvoices(h),
      api.revAITransactions(h),
      api.revListPlans(h),
      api.revPaymentHistory(h),
    ]);

    // sub is the critical call — if it fails, show an error state instead of
    // an infinite spinner.
    if (sub.status === 'fulfilled') {
      setData(sub.value.data);
    } else {
      // Set a minimal safe default so the page renders (not a spinner).
      setData({
        exempt: false,
        plan: null,
        subscription: null,
        ai_wallet: { purchased_credits: 0, consumed_credits: 0, available_credits: 0 },
        ocr_wallet: { purchased_pages: 0, consumed_pages: 0, available_pages: 0 },
        storage_wallet: { allocated_gb: 0, used_gb: 0 },
      });
    }

    // packages — if billing.py is unavailable, set null so the Top Up section
    // is simply hidden (the !exempt && packages && ... guard already handles null).
    setPackages(pkg.status === 'fulfilled' ? pkg.value.data : null);

    // invoices and transactions — safe to default to empty arrays.
    setInvoices(inv.status === 'fulfilled' ? (inv.value.data || []) : []);
    setTxns(tx.status === 'fulfilled' ? (tx.value.data || []) : []);

    // plans — safe to default to empty array (upgrade section just won't show).
    setPlans(pl.status === 'fulfilled' ? (pl.value.data || []) : []);

    // payment history — safe to default to empty array.
    setPayments(pay.status === 'fulfilled' ? (pay.value.data || []) : []);

    window.dispatchEvent(new Event('credits:refresh'));
  }, []);

  // Handle Stripe return (poll status)
  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts === 0) setPolling(true);
    if (attempts >= 6) { setPolling(false); toast.error('Payment status check timed out.'); return; }
    try {
      const res = await api.revCheckoutStatus(sessionId, getAuthHeaders());
      if (res.data.payment_status === 'paid') {
        setPolling(false);
        toast.success(`Payment successful — ${res.data.label || 'purchase'} added!`);
        window.history.replaceState({}, '', '/billing');
        load();
        return;
      }
      if (res.data.status === 'expired') { setPolling(false); toast.error('Payment session expired.'); return; }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch (e) { setPolling(false); }
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (sid) pollStatus(sid);
    load();
  }, []);

  // Deep-link from the quota-exceeded modal: /billing?focus=credits|ocr|plans
  useEffect(() => {
    const focus = new URLSearchParams(window.location.search).get('focus');
    if (!focus || !data) return;
    const id = focus === 'ocr' ? 'focus-ocr' : focus === 'credits' ? 'focus-credits' : 'focus-plans';
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-amber-400', 'ring-offset-2', 'rounded-xl');
        setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400', 'ring-offset-2', 'rounded-xl'), 2600);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [data, packages]);

  const buy = async (packageId) => {
    setBuying(packageId);
    try {
      const res = await api.revCheckout(
        { package_id: packageId, origin_url: window.location.origin },
        getAuthHeaders());
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((d && (d.message || (typeof d === 'string' ? d : null))) || 'Could not start checkout');
      setBuying('');
    }
  };

  const buyPlan = async (planCode) => {
    setBuying(planCode);
    try {
      const res = await api.revCheckout(
        { package_id: planCode, billing_cycle: cycle, origin_url: window.location.origin },
        getAuthHeaders());
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((d && (d.message || (typeof d === 'string' ? d : null))) || 'Could not start checkout');
      setBuying('');
    }
  };

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const buyRazorpay = async (packageId, isPlan = false) => {
    setBuying(`rzp-${packageId}`);
    try {
      const payload = isPlan
        ? { package_id: packageId, billing_cycle: cycle }
        : { package_id: packageId };
      const res = await api.revRazorpayCheckout(payload, getAuthHeaders());
      const data = res.data;

      if (data.mock) {
        // DEMO gateway (no keys configured): simulate success.
        await api.revRazorpayConfirm(data.order_id, getAuthHeaders());
        toast.success('Payment successful (Razorpay demo) — wallet updated');
        await load();
        setBuying('');
        return;
      }

      // REAL Razorpay Test/Live Mode: open the Checkout.js popup.
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) { toast.error('Could not load Razorpay checkout. Check your connection.'); setBuying(''); return; }
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: data.name || 'ReasonDMS',
        description: data.description || data.label,
        order_id: data.order_id,
        prefill: data.prefill || {},
        theme: { color: brandColors.primary },
        handler: async (resp) => {
          try {
            await api.revRazorpayVerify({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            }, getAuthHeaders());
            toast.success('Payment verified — wallet updated');
            await load();
          } catch (err) {
            toast.error(err.response?.data?.detail?.message || 'Payment verification failed');
          } finally { setBuying(''); }
        },
        modal: { ondismiss: () => setBuying('') },
      });
      rzp.on('payment.failed', (r) => {
        toast.error(r?.error?.description || 'Payment failed');
        setBuying('');
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Razorpay checkout failed');
      setBuying('');
    }
  };

  const downloadInvoice = async (inv) => {
    try {
      const res = await api.revInvoicePdf(inv.id, getAuthHeaders());
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${(inv.invoice_number || 'invoice').replace(/\//g, '-')}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const downloadReceipt = async (p) => {
    try {
      const res = await api.revReceiptPdf(p.id || p.session_id, getAuthHeaders());
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `receipt-${(p.session_id || 'payment')}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Receipt available only for paid payments'); }
  };

  if (!data) {
    return <div className="flex items-center justify-center py-20 text-slate-500">
      <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading subscription…</div>;
  }

  const exempt = data.exempt;
  const plan = data.plan;
  const ai = data.ai_wallet || {};
  const ocr = data.ocr_wallet || {};
  const storage = data.storage_wallet || {};

  const pct = (used, total) => total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const stripeEnabled = !!packages?.gateways?.stripe;

  return (
    <div data-testid="billing-page" className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: brandColors.primary }}>Billing &amp; Plan</h1>
        <p className="text-slate-600 mt-1">Manage your subscription, AI credits, OCR pages and invoices.</p>
      </div>

      {polling && (
        <div className="flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-200 p-3 text-blue-700" data-testid="payment-polling">
          <Loader2 className="w-4 h-4 animate-spin" /> Confirming your payment…
        </div>
      )}

      {/* Current plan */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Current Plan</span>
            {exempt
              ? <Badge className="bg-emerald-100 text-emerald-700">Master · Unlimited</Badge>
              : <Badge style={{ backgroundColor: brandColors.primary }}>{plan?.plan_name || 'No Plan'}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {exempt ? (
            <p className="text-slate-600 flex items-center gap-2">
              <InfinityIcon className="w-5 h-5 text-emerald-600" /> Your tenant has unlimited access to all features and credits.
            </p>
          ) : plan ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Info label="Plan" value={plan.plan_name} />
              <Info label="Monthly" value={fmtINR(plan.monthly_price)} />
              <Info label="Billing" value={data.subscription?.billing_cycle || '—'} />
              <Info label="Max Users" value={plan.max_users < 0 ? '∞' : plan.max_users} />
            </div>
          ) : (
            <p className="text-slate-500">No active subscription. Choose a plan below to get started.</p>
          )}
        </CardContent>
      </Card>

      {/* Wallets */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <WalletCard icon={Coins} color="text-amber-500" title="AI Credits"
          exempt={exempt} testid="wallet-ai"
          used={ai.consumed_credits} total={ai.purchased_credits}
          available={ai.available_credits} unit="credits" pct={pct} />
        <WalletCard icon={FileScan} color="text-indigo-500" title="OCR Pages"
          exempt={exempt} testid="wallet-ocr"
          used={ocr.consumed_pages} total={ocr.purchased_pages}
          available={ocr.available_pages} unit="pages" pct={pct} />
        <Card data-testid="wallet-storage">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-teal-500" /> Storage
            </CardTitle>
          </CardHeader>
          <CardContent>
            {exempt ? <p className="text-emerald-700 flex items-center gap-1"><InfinityIcon className="w-4 h-4" /> Unlimited</p> : (
              <>
                <div className="flex items-end justify-between mb-2">
                  <span className="text-2xl font-bold text-slate-900">{storage.used_gb ?? 0} GB</span>
                  <span className="text-sm text-slate-400">/ {storage.allocated_gb ?? 0} GB</span>
                </div>
                <Progress value={pct(storage.used_gb, storage.allocated_gb)} className="h-2" />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Resource Utilisation overview */}
      {!exempt && (
        <Card data-testid="utilisation-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Gauge className="w-5 h-5 text-slate-500" /> Resource Utilisation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <UtilBar label="AI Credits" used={ai.consumed_credits} total={ai.purchased_credits} unit="credits" pct={pct} testid="util-ai" />
            <UtilBar label="OCR Pages" used={ocr.consumed_pages} total={ocr.purchased_pages} unit="pages" pct={pct} testid="util-ocr" />
            <UtilBar label="Storage" used={storage.used_gb} total={storage.allocated_gb} unit="GB" pct={pct} testid="util-storage" />
            {data.workflow_usage && (
              data.workflow_usage.limit > 0
                ? <UtilBar label="Workflows" used={data.workflow_usage.used} total={data.workflow_usage.limit} unit="workflows" pct={pct} testid="util-workflows" />
                : <UtilRowUnlimited label="Workflows" used={data.workflow_usage.used} unit="workflows" testid="util-workflows" />
            )}
            {data.api_usage && (
              data.api_usage.limit > 0
                ? <UtilBar label="API Calls (this month)" used={data.api_usage.used} total={data.api_usage.limit} unit="calls" pct={pct} testid="util-api" />
                : <UtilRowUnlimited label="API Calls (this month)" used={data.api_usage.used} unit="calls" testid="util-api" />
            )}
            {plan && (
              <div className="flex items-center gap-2 text-sm text-slate-500 pt-1">
                <Users className="w-4 h-4" /> Plan seats: {plan.max_users < 0 ? '∞' : plan.max_users} users
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upgrade / Change Plan */}
      {!exempt && plans.length > 0 && (
        <Card data-testid="upgrade-plans-card" id="focus-plans">
          <CardHeader>
            <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <span>Choose a Plan</span>
              <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 w-fit" data-testid="billing-cycle-toggle">
                <button onClick={() => setCycle('monthly')} data-testid="cycle-monthly"
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${cycle === 'monthly' ? 'bg-white shadow font-semibold text-slate-900' : 'text-slate-500'}`}>Monthly</button>
                <button onClick={() => setCycle('yearly')} data-testid="cycle-yearly"
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${cycle === 'yearly' ? 'bg-white shadow font-semibold text-slate-900' : 'text-slate-500'}`}>Yearly</button>
                {(() => {
                  const maxSave = Math.max(0, ...plans.filter((p) => p.plan_code !== 'MASTER').map(yearlySavingsPct));
                  return maxSave > 0 ? (
                    <Badge data-testid="annual-savings-badge"
                      className="ml-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                      Save up to {maxSave}%
                    </Badge>
                  ) : null;
                })()}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans
                .filter((p) => p.plan_code !== 'MASTER' && (cycle === 'yearly' ? p.yearly_price : p.monthly_price) > 0)
                .map((p) => (
                  <PlanCard key={p.plan_code} plan={p}
                    price={cycle === 'yearly' ? p.yearly_price : p.monthly_price}
                    cycle={cycle} isCurrent={data.subscription?.plan_code === p.plan_code}
                    buying={buying} onBuy={buyPlan} onBuyRazorpay={buyRazorpay} stripeEnabled={stripeEnabled} />
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Buy packages */}
      {!exempt && packages && (
        <Card>
          <CardHeader><CardTitle>Top Up</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div id="focus-credits">
              <p className="text-sm font-semibold text-slate-600 mb-2">AI Credit Packs</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {Object.entries(packages.credit_packages).map(([id, p]) => (
                  <PackageCard key={id} id={id} pkg={p} buying={buying} onBuy={buy} onBuyRazorpay={buyRazorpay} testidPrefix="buy-credits" stripeEnabled={stripeEnabled} />
                ))}
              </div>
            </div>
            <div id="focus-ocr">
              <p className="text-sm font-semibold text-slate-600 mb-2">OCR Page Packs</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(packages.ocr_packages).map(([id, p]) => (
                  <PackageCard key={id} id={id} pkg={p} buying={buying} onBuy={buy} onBuyRazorpay={buyRazorpay} testidPrefix="buy-ocr" stripeEnabled={stripeEnabled} />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoices */}
      <Card>
        <CardHeader><CardTitle>Invoices</CardTitle></CardHeader>
        <CardContent>
          {invoices.length === 0 ? <p className="text-slate-500 text-sm">No invoices yet.</p> : (
            <Table data-testid="invoices-table">
              <TableHeader><TableRow>
                <TableHead>Invoice #</TableHead><TableHead>Date</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                    <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                    <TableCell>{String(inv.issue_date).slice(0, 10)}</TableCell>
                    <TableCell className="text-right">{fmtINR(inv.grand_total)}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{inv.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => downloadInvoice(inv)} data-testid={`download-invoice-${inv.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-slate-500" /> Payment History</CardTitle></CardHeader>
        <CardContent>
          {payments.length === 0 ? <p className="text-slate-500 text-sm">No payments yet.</p> : (
            <Table data-testid="payments-table">
              <TableHeader><TableRow>
                <TableHead>Item</TableHead><TableHead>Gateway</TableHead><TableHead>Date</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {payments.map((p) => (
                  <TableRow key={p.id || p.session_id} data-testid={`payment-row-${p.id || p.session_id}`}>
                    <TableCell className="font-medium">{p.label}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize flex items-center gap-1 w-fit">
                        <CreditCard className="w-3 h-3" /> {p.gateway}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500">{fmtISTDate(p.created_at)}</TableCell>
                    <TableCell className="text-right">{fmtINR(p.amount)}</TableCell>
                    <TableCell>
                      <Badge className={`capitalize ${p.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {p.payment_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {p.payment_status === 'paid' && (
                        <Button size="sm" variant="ghost" onClick={() => downloadReceipt(p)} data-testid={`download-receipt-${p.id || p.session_id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Recent AI usage */}
      <Card>
        <CardHeader><CardTitle>Recent AI Usage</CardTitle></CardHeader>
        <CardContent>
          {txns.length === 0 ? <p className="text-slate-500 text-sm">No usage yet.</p> : (
            <Table data-testid="usage-table">
              <TableHeader><TableRow>
                <TableHead>Action</TableHead><TableHead>When</TableHead>
                <TableHead className="text-right">Credits</TableHead><TableHead className="text-right">Balance</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {txns.slice(0, 12).map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{t.action_name}</TableCell>
                    <TableCell className="text-slate-500">{fmtIST(t.timestamp)}</TableCell>
                    <TableCell className="text-right text-red-600">-{t.credits_consumed}</TableCell>
                    <TableCell className="text-right">{t.credits_after}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div><p className="text-xs text-slate-500">{label}</p><p className="font-semibold text-slate-900 capitalize">{value}</p></div>
);

const UtilBar = ({ label, used, total, unit, pct, testid }) => {
  const p = pct(used, total);
  const bar = p >= 90 ? 'bg-red-500' : p >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="text-slate-500">
          {(used ?? 0).toLocaleString()} / {(total ?? 0).toLocaleString()} {unit}
          <span className="ml-2 font-semibold text-slate-900" data-testid={`${testid}-pct`}>{p}%</span>
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${bar}`} style={{ width: `${p}%` }} />
      </div>
    </div>
  );
};

const UtilRowUnlimited = ({ label, used, unit, testid }) => (
  <div data-testid={testid} className="flex items-center justify-between text-sm">
    <span className="font-medium text-slate-700">{label}</span>
    <span className="text-slate-500">
      {(used ?? 0).toLocaleString()} {unit} used
      <span className="ml-2 font-semibold text-emerald-600">Unlimited</span>
    </span>
  </div>
);

const WalletCard = ({ icon: Icon, color, title, exempt, used, total, available, unit, pct, testid }) => (
  <Card data-testid={testid}>
    <CardHeader className="pb-2">
      <CardTitle className="text-base flex items-center gap-2"><Icon className={`w-5 h-5 ${color}`} /> {title}</CardTitle>
    </CardHeader>
    <CardContent>
      {exempt ? <p className="text-emerald-700 flex items-center gap-1"><InfinityIcon className="w-4 h-4" /> Unlimited</p> : (
        <>
          <div className="flex items-end justify-between mb-2">
            <span className="text-2xl font-bold text-slate-900">{(available ?? 0).toLocaleString()}</span>
            <span className="text-sm text-slate-400">/ {(total ?? 0).toLocaleString()} {unit}</span>
          </div>
          <Progress value={pct(used, total)} className="h-2" />
          <p className="text-xs text-slate-400 mt-1">{(used ?? 0).toLocaleString()} {unit} used</p>
        </>
      )}
    </CardContent>
  </Card>
);

const PlanCard = ({ plan, price, cycle, isCurrent, buying, onBuy, onBuyRazorpay, stripeEnabled }) => {
  const isBusy = buying === plan.plan_code || buying === `rzp-${plan.plan_code}`;
  return (
  <div data-testid={`plan-card-${plan.plan_code}`}
    className={`rounded-xl border p-5 flex flex-col gap-3 transition-all ${isCurrent ? 'border-emerald-400 bg-emerald-50/40' : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'}`}>
    <div className="flex items-center justify-between">
      <p className="font-bold text-lg text-slate-900">{plan.plan_name}</p>
      {isCurrent && <Badge className="bg-emerald-100 text-emerald-700">Current</Badge>}
    </div>
    <div>
      <span className="text-3xl font-bold" style={{ color: brandColors.primary }}>{fmtINR(price)}</span>
      <span className="text-sm text-slate-400">/{cycle === 'yearly' ? 'yr' : 'mo'}</span>
    </div>
    {cycle === 'yearly' && yearlySavingsPct(plan) > 0 && (
      <div data-testid={`plan-savings-${plan.plan_code}`} className="text-xs font-medium text-emerald-600">
        Save {yearlySavingsPct(plan)}% vs monthly ({fmtINR(plan.monthly_price * 12 - plan.yearly_price)}/yr)
      </div>
    )}
    <ul className="text-xs text-slate-500 space-y-1 flex-1">
      <li>{plan.max_ai_credits < 0 ? '∞' : Number(plan.max_ai_credits).toLocaleString()} AI credits</li>
      <li>{plan.max_ocr_pages < 0 ? '∞' : Number(plan.max_ocr_pages).toLocaleString()} OCR pages</li>
      <li>{plan.max_storage_gb < 0 ? '∞' : plan.max_storage_gb} GB storage</li>
      <li>{plan.max_users < 0 ? '∞' : plan.max_users} users</li>
    </ul>
    <Button disabled={isCurrent || !!buying}
      onClick={() => stripeEnabled ? onBuy(plan.plan_code) : onBuyRazorpay(plan.plan_code, true)}
      data-testid={`subscribe-${plan.plan_code}`}
      variant={isCurrent ? 'outline' : 'default'}
      style={isCurrent ? undefined : { backgroundColor: brandColors.primary }}>
      {isBusy ? <Loader2 className="w-4 h-4 animate-spin" />
        : isCurrent ? 'Active' : (stripeEnabled ? 'Subscribe' : 'Subscribe')}
    </Button>
    {!isCurrent && stripeEnabled && onBuyRazorpay && (
      <button disabled={!!buying} onClick={() => onBuyRazorpay(plan.plan_code, true)}
        data-testid={`subscribe-razorpay-${plan.plan_code}`}
        className="text-xs text-slate-500 hover:text-slate-800 underline disabled:opacity-50">
        {buying === `rzp-${plan.plan_code}` ? 'Processing…' : 'or pay via Razorpay'}
      </button>
    )}
  </div>
  );
};

const PackageCard = ({ id, pkg, buying, onBuy, onBuyRazorpay, testidPrefix, stripeEnabled }) => {
  const isBusy = buying === id || buying === `rzp-${id}`;
  return (
  <div className="rounded-lg border border-slate-200 p-4 flex flex-col gap-2 hover:border-slate-300 transition-colors">
    <p className="font-semibold text-slate-900">{pkg.label}</p>
    <p className="text-2xl font-bold" style={{ color: brandColors.primary }}>{fmtINR(pkg.amount)}</p>
    <Button onClick={() => stripeEnabled ? onBuy(id) : onBuyRazorpay(id, false)} disabled={!!buying} data-testid={`${testidPrefix}-${id}`}
      className="mt-1" style={{ backgroundColor: brandColors.primary }}>
      {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> {stripeEnabled ? 'Buy' : 'Buy'}</>}
    </Button>
    {stripeEnabled && onBuyRazorpay && (
      <button disabled={!!buying} onClick={() => onBuyRazorpay(id, false)}
        data-testid={`${testidPrefix}-razorpay-${id}`}
        className="text-xs text-slate-500 hover:text-slate-800 underline disabled:opacity-50">
        {buying === `rzp-${id}` ? 'Processing…' : 'or pay via Razorpay'}
      </button>
    )}
  </div>
  );
};

export default TenantSubscription;
